import {useMemo} from 'react';
import {useQuery} from '@apollo/client';
import {GET_EXCHANGE_SUMMARY} from 'pages/exchanges/resolvers';

/**
 * Brokers are venue-scoped: one record serves exactly one exchange and settles
 * in that exchange's currency. A broker that trades several venues has one
 * record per venue, so the Daba master now exists twice — once for BRVM, once
 * for NGX — and a name on its own no longer identifies a broker.
 *
 * Two consequences run through the UI:
 *
 * 1. Every broker picker has to say which venue a row is for, or the two Daba
 *    rows read as duplicates.
 * 2. `exchangeCode` is nullable in the schema only so the change did not break
 *    existing clients. Venue matching is an exact equality check, so a broker
 *    with no `exchangeCode` matches no venue and is never assigned an order —
 *    it is dead weight. Treat it as required everywhere.
 */

/** Sectors, the composite index and movers are the expensive part of the
 * exchange read, and none of them matter for a venue dropdown. */
const VENUE_INPUT = {
  includeSectors: false,
  includeCompositeIndex: false,
  moversLimit: 0,
};

/**
 * The venues a broker can be scoped to, sourced from the exchanges list so the
 * codes cannot drift from the real ones. `currentCode` keeps an existing
 * broker's venue selectable even if that exchange has since gone from the list,
 * so opening the edit form never silently blanks a saved value.
 */
export const useVenueOptions = (currentCode = null) => {
  const {data, loading} = useQuery(GET_EXCHANGE_SUMMARY, {
    variables: {input: VENUE_INPUT},
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const options = useMemo(() => {
    // The node with no exchange is the All markets aggregate, not a venue.
    const venues = (data?.getExchanges?.data ?? [])
      .map(node => node.exchange)
      .filter(exchange => exchange?.code)
      .map(exchange => ({
        code: exchange.code,
        name: exchange.stockExchangeName || exchange.code,
        currency: exchange.baseCurrencyCode || null,
      }));

    if (currentCode && !venues.some(venue => venue.code === currentCode)) {
      // Flagged so callers can tell "this venue has no base currency" from
      // "we know nothing about this venue" and leave a saved value alone.
      venues.push({
        code: currentCode,
        name: currentCode,
        currency: null,
        unknown: true,
      });
    }

    return venues;
  }, [data, currentCode]);

  return {venueOptions: options, loadingVenues: loading};
};

/**
 * How a broker reads in a dropdown. Venue first after the name, because that is
 * what separates the two Daba rows; asset class stays because most pickers are
 * still chosen by it.
 */
export const brokerOptionLabel = broker => {
  if (!broker) return '';
  const venue = broker.exchangeCode || 'No venue';
  const assetClass = broker.assetClass
    ? broker.assetClass.replace(/_/g, ' ')
    : null;
  return assetClass
    ? `${broker.name} — ${venue} · ${assetClass}`
    : `${broker.name} — ${venue}`;
};
